import { useMemo } from 'react';

import { useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  brainsEndpoints,
  collectionsEndpoints,
  datasourceEndpoints,
  desksEndpoints,
  documentsEndpoints,
  membersEndpoints,
} from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import {
  GOAL_ACHIEVED_LABEL_KEYS,
  USER_SENTIMENT_LABEL_KEYS,
  memberLabel,
  type GoalAchievedValue,
  type UserSentimentValue,
} from '@/modules/analytics/hooks/useFilterColumnOptions';
import { selectAccountId, selectAccountSlug } from '@/redux/session/selectors';
import type { Brains } from '@/shared/types/brain';
import type { Collections, Datasources, Document } from '@/shared/types/collections';
import type { Desk } from '@/shared/types/desk';
import type { Members } from '@/shared/types/member';
import type { MetricsQueryRow } from '@/shared/types/metricsQuery';

type DesksResponse = { desks: Desk[] };

export type GroupLabelResolver = (key: string) => string;

export type ResolvedGroupLabel = { label: string; isMissing: boolean };

export type MultiGroupLabelResolver = (column: string, key: string) => ResolvedGroupLabel;

const ONE_MINUTE = 60_000;

const identity: GroupLabelResolver = (key) => key;

const isGoalAchievedKey = (key: string): key is GoalAchievedValue =>
  key in GOAL_ACHIEVED_LABEL_KEYS;

const isUserSentimentKey = (key: string): key is UserSentimentValue =>
  key in USER_SENTIMENT_LABEL_KEYS;

// Matches the placeholder produced by formatGroupKey() in useReportMetric.ts
// for NULL groupBy values. Kept as a literal here (rather than importing) to
// avoid a circular dep between the two hook files.
const EMPTY_GROUP_KEY = '∅';

// GroupBy columns whose NULL bucket should be labelled "Not classified" —
// LLM-judge outputs (goal_achieved, user_sentiment, csat_brain) where a
// missing value means the judge did not assign a classification, plus the
// human-rated CSAT score (csat_human_agent) where NULL means the customer
// did not leave a rating. Other dimensions (channel, desk_id, …) keep the
// raw ∅ symbol because their NULL has a different meaning (no channel
// attribution, no desk, etc.).
const CLASSIFIED_COLUMNS: ReadonlySet<string> = new Set([
  'goal_achieved',
  'user_sentiment',
  'csat_brain',
  'csat_human_agent',
]);

const extractStringCell = (raw: unknown): string | null =>
  typeof raw === 'string' && raw.length > 0 ? raw : null;

// Walk the metric rows once and pull out the unique collection_ids and
// (collection_id, datasource_id, document_id) triples that actually appear.
// We only fan out lookups for IDs the table is going to render, which keeps
// the request count proportional to the visible row set rather than the
// account's whole knowledge base.
const collectKnowledgeIds = (rows: MetricsQueryRow[] | undefined) => {
  const collectionIds = new Set<string>();
  const datasourcePairs = new Map<string, { collectionId: string; datasourceId: string }>();
  const documentTriples = new Map<
    string,
    { collectionId: string; datasourceId: string; documentId: string }
  >();
  for (const row of rows ?? []) {
    const cid = extractStringCell(row.collection_id);
    const dsid = extractStringCell(row.datasource_id);
    const did = extractStringCell(row.document_id);
    if (cid) {
      collectionIds.add(cid);
    }
    if (cid && dsid) {
      datasourcePairs.set(`${cid}:${dsid}`, { collectionId: cid, datasourceId: dsid });
    }
    if (cid && dsid && did) {
      documentTriples.set(`${cid}:${dsid}:${did}`, {
        collectionId: cid,
        datasourceId: dsid,
        documentId: did,
      });
    }
  }
  return {
    collectionIds: [...collectionIds],
    datasourcePairs: [...datasourcePairs.values()],
    documentTriples: [...documentTriples.values()],
  };
};

type KnowledgeBaseMaps = {
  collections: Map<string, string>;
  collectionsLoaded: boolean;
  datasources: Map<string, string>;
  datasourcesLoaded: boolean;
  documents: Map<string, string>;
  documentsLoaded: boolean;
};

const useKnowledgeBaseNameMaps = (
  groupColumns: string[],
  rows: MetricsQueryRow[] | undefined
): KnowledgeBaseMaps => {
  const accountId = useSelector(selectAccountId);
  const needsCollections = groupColumns.includes('collection_id');
  const needsDatasources = groupColumns.includes('datasource_id');
  const needsDocuments = groupColumns.includes('document_id');
  const needsAnyKnowledgeBase = needsCollections || needsDatasources || needsDocuments;

  // Collections are flat-listed in a single endpoint, so fetch them whenever
  // any KB dimension is grouped on — collection names are the cheapest hop
  // and they double as the fallback parent for datasource/document fan-outs.
  const { data: collectionsData, isSuccess: collectionsLoaded } = useQuery<Collections>({
    queryKey: [collectionsEndpoints.collections],
    queryFn: () => callGet(collectionsEndpoints.collections),
    enabled: !!accountId && needsAnyKnowledgeBase,
    staleTime: ONE_MINUTE,
  });

  const { datasourcePairs, documentTriples } = useMemo(() => collectKnowledgeIds(rows), [rows]);

  // Datasources live under /collections/:id/datasources. Use the unique
  // collection_ids surfaced by the metric rows; if datasource_id is grouped
  // without collection_id (no parent context in rows), fall back to fanning
  // out across every collection so names still resolve.
  const datasourceCollectionIds = useMemo(() => {
    if (!needsDatasources) {
      return [] as string[];
    }
    const fromRows = new Set(datasourcePairs.map((p) => p.collectionId));
    if (fromRows.size > 0) {
      return [...fromRows];
    }
    return (collectionsData?.collections ?? []).map((c) => c.collection_id);
  }, [needsDatasources, datasourcePairs, collectionsData]);

  const datasourceQueries = useQueries({
    queries: datasourceCollectionIds.map((collectionId) => ({
      queryKey: [datasourceEndpoints.datasources(collectionId)],
      queryFn: (): Promise<Datasources> => callGet(datasourceEndpoints.datasources(collectionId)),
      staleTime: ONE_MINUTE,
      enabled: !!accountId,
    })),
  });

  const documentQueries = useQueries({
    queries: documentTriples.map(({ collectionId, datasourceId, documentId }) => ({
      queryKey: [documentsEndpoints.document(collectionId, datasourceId, documentId)],
      queryFn: (): Promise<Document> =>
        callGet(documentsEndpoints.document(collectionId, datasourceId, documentId)),
      staleTime: ONE_MINUTE,
      enabled: !!accountId && needsDocuments,
    })),
  });

  const collections = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of collectionsData?.collections ?? []) {
      map.set(c.collection_id, c.name);
    }
    return map;
  }, [collectionsData]);

  const datasources = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of datasourceQueries) {
      for (const ds of q.data?.datasources ?? []) {
        map.set(ds.datasource_id, ds.name);
      }
    }
    return map;
  }, [datasourceQueries]);

  const documents = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of documentQueries) {
      if (q.data?.document_id) {
        map.set(q.data.document_id, q.data.name);
      }
    }
    return map;
  }, [documentQueries]);

  const settled = (queries: { isSuccess: boolean }[]) =>
    queries.length > 0 && queries.every((q) => q.isSuccess);

  return {
    collections,
    collectionsLoaded,
    datasources,
    datasourcesLoaded: settled(datasourceQueries),
    documents,
    documentsLoaded: settled(documentQueries),
  };
};

export const useGroupLabelResolver = (
  groupColumn: string | undefined,
  rows?: MetricsQueryRow[]
): GroupLabelResolver => {
  const accountId = useSelector(selectAccountId);
  const { t } = useTranslation();

  const { data: brains } = useQuery<Brains>({
    queryKey: [brainsEndpoints.brains],
    queryFn: () => callGet(brainsEndpoints.brains),
    enabled: !!accountId && groupColumn === 'brain_parent_id',
    staleTime: ONE_MINUTE,
  });

  const { data: desks } = useQuery<DesksResponse>({
    queryKey: [desksEndpoints.desks],
    queryFn: () => callGet(desksEndpoints.desks),
    enabled: !!accountId && groupColumn === 'desk_id',
    staleTime: ONE_MINUTE,
  });

  const accountSlug = useSelector(selectAccountSlug);
  const { data: members } = useQuery<Members>({
    queryKey: [membersEndpoints.members, accountSlug],
    queryFn: () => callGet(membersEndpoints.members),
    enabled: !!accountSlug && groupColumn === 'agent_id',
    staleTime: ONE_MINUTE,
  });

  const kb = useKnowledgeBaseNameMaps(groupColumn ? [groupColumn] : [], rows);

  return useMemo<GroupLabelResolver>(() => {
    if (groupColumn === 'brain_parent_id') {
      const map = new Map((brains?.brains ?? []).map((b) => [b.brain_parent_id, b.name] as const));
      return (key) => map.get(key) ?? key;
    }
    if (groupColumn === 'desk_id') {
      const map = new Map((desks?.desks ?? []).map((d) => [d.desk_id, d.name] as const));
      return (key) => map.get(key) ?? key;
    }
    if (groupColumn === 'agent_id') {
      const map = new Map(
        (members?.members ?? []).map((m) => [m.user_id, memberLabel(m)] as const)
      );
      return (key) => map.get(key) ?? key;
    }
    if (groupColumn === 'collection_id') {
      return (key) => kb.collections.get(key) ?? key;
    }
    if (groupColumn === 'datasource_id') {
      return (key) => kb.datasources.get(key) ?? key;
    }
    if (groupColumn === 'document_id') {
      return (key) => kb.documents.get(key) ?? key;
    }
    if (groupColumn === 'goal_achieved') {
      return (key) => {
        if (key === EMPTY_GROUP_KEY) {
          return t('analytics.not_classified');
        }
        return isGoalAchievedKey(key) ? t(GOAL_ACHIEVED_LABEL_KEYS[key]) : key;
      };
    }
    if (groupColumn === 'user_sentiment') {
      return (key) => {
        if (key === EMPTY_GROUP_KEY) {
          return t('analytics.not_classified');
        }
        return isUserSentimentKey(key) ? t(USER_SENTIMENT_LABEL_KEYS[key]) : key;
      };
    }
    if (groupColumn && CLASSIFIED_COLUMNS.has(groupColumn)) {
      return (key) => (key === EMPTY_GROUP_KEY ? t('analytics.not_classified') : key);
    }
    return identity;
  }, [groupColumn, brains, desks, members, kb, t]);
};

export const useMultiGroupLabelResolver = (
  groupColumns: string[],
  rows?: MetricsQueryRow[]
): MultiGroupLabelResolver => {
  const accountId = useSelector(selectAccountId);
  const accountSlug = useSelector(selectAccountSlug);
  const { t } = useTranslation();
  const needsBrains = groupColumns.includes('brain_parent_id');
  const needsDesks = groupColumns.includes('desk_id');
  const needsMembers = groupColumns.includes('agent_id');

  const { data: brains, isSuccess: brainsLoaded } = useQuery<Brains>({
    queryKey: [brainsEndpoints.brains],
    queryFn: () => callGet(brainsEndpoints.brains),
    enabled: !!accountId && needsBrains,
    staleTime: ONE_MINUTE,
  });

  const { data: desks, isSuccess: desksLoaded } = useQuery<DesksResponse>({
    queryKey: [desksEndpoints.desks],
    queryFn: () => callGet(desksEndpoints.desks),
    enabled: !!accountId && needsDesks,
    staleTime: ONE_MINUTE,
  });

  const { data: members, isSuccess: membersLoaded } = useQuery<Members>({
    queryKey: [membersEndpoints.members, accountSlug],
    queryFn: () => callGet(membersEndpoints.members),
    enabled: !!accountSlug && needsMembers,
    staleTime: ONE_MINUTE,
  });

  const kb = useKnowledgeBaseNameMaps(groupColumns, rows);

  return useMemo<MultiGroupLabelResolver>(() => {
    const brainsMap = new Map(
      (brains?.brains ?? []).map((b) => [b.brain_parent_id, b.name] as const)
    );
    const desksMap = new Map((desks?.desks ?? []).map((d) => [d.desk_id, d.name] as const));
    const membersMap = new Map(
      (members?.members ?? []).map((m) => [m.user_id, memberLabel(m)] as const)
    );
    const lookup = (map: Map<string, string>, key: string, loaded: boolean): ResolvedGroupLabel => {
      const label = map.get(key);
      if (label) {
        return { label, isMissing: false };
      }
      return { label: key, isMissing: loaded };
    };

    return (column, key) => {
      if (column === 'brain_parent_id') {
        return lookup(brainsMap, key, brainsLoaded);
      }
      if (column === 'desk_id') {
        return lookup(desksMap, key, desksLoaded);
      }
      if (column === 'agent_id') {
        return lookup(membersMap, key, membersLoaded);
      }
      if (column === 'collection_id') {
        return lookup(kb.collections, key, kb.collectionsLoaded);
      }
      if (column === 'datasource_id') {
        return lookup(kb.datasources, key, kb.datasourcesLoaded);
      }
      if (column === 'document_id') {
        return lookup(kb.documents, key, kb.documentsLoaded);
      }
      if (CLASSIFIED_COLUMNS.has(column) && key === EMPTY_GROUP_KEY) {
        return { label: t('analytics.not_classified'), isMissing: false };
      }
      if (column === 'goal_achieved' && isGoalAchievedKey(key)) {
        return { label: t(GOAL_ACHIEVED_LABEL_KEYS[key]), isMissing: false };
      }
      if (column === 'user_sentiment' && isUserSentimentKey(key)) {
        return { label: t(USER_SENTIMENT_LABEL_KEYS[key]), isMissing: false };
      }
      return { label: key, isMissing: false };
    };
  }, [brains, brainsLoaded, desks, desksLoaded, members, membersLoaded, kb, t]);
};
